import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import {
  Box, Paper, Typography, Divider, TextField, Button, 
  FormControl, InputLabel, Select, MenuItem, Snackbar, 
  Alert, LinearProgress, Stack, Switch, FormGroup, 
  FormControlLabel, IconButton
} from "@mui/material";

// Icons
import {
  LabelOutlined as LabelIcon,
  CloudUploadOutlined as UploadIcon,
  DeleteOutline as DeleteIcon,
  SaveOutlined as SaveIcon,
  InfoOutlined as InfoIcon,
  SettingsOutlined as SettingsIcon
} from "@mui/icons-material";

import {
  getLabel,
  postlabel,
  resetLabelState,
} from "./labelSettingsSlice";

// --- Enterprise Styled Components ---

const SettingsContainer = styled(Paper)(({ theme }) => ({
  borderRadius: '16px',
  border: 'none',
  boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
  padding: theme.spacing(4),
  backgroundColor: '#FFFFFF',
  position: 'relative'
}));

const ConfigWrapper = styled(Box)(({ theme }) => ({
  // maxWidth: '650px',
  margin: '0 auto',
  padding: theme.spacing(4),
  backgroundColor: '#F8FAFC', // Slate-ice blue tint
  borderRadius: '16px',
  border: '1px solid #E2E8F0',
}));

const HiddenInput = styled("input")({
  display: "none",
});

const SectionHeader = ({ icon, title, subtitle }) => (
  <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
    <Box sx={{ p: 1.5, bgcolor: 'rgba(25, 118, 210, 0.08)', borderRadius: '12px', color: 'primary.main' }}>
      {icon}
    </Box>
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 800, color: '#1E293B', lineHeight: 1.2 }}>
        {title}
      </Typography>
      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>
        {subtitle}
      </Typography>
    </Box>
  </Box>
);

const LabelSettingsPage = () => {
  // --- Existing Logic & States Preserved ---
  const dispatch = useDispatch();
  const labelsettings = useSelector((state) => state.labelsettings);
  const profile = useSelector(state => state.auth.profile);  
  
  const [alert, setAlert] = useState(false);
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("pdf");
  const [fileUpload, setFileUpload] = useState("");
  const [fileName, setFileName] = useState("");
  const [checkRemove, setCheckRemove] = useState(true);
  const [logoDelete, setLogoDelete] = useState(false);

  const initialValues = {
    is_signature_required: false,
    is_detachable_label: false,
    order_number: false,
    tracking_number: false,
    reference_number: false,
    carrier_name: false,
    weight: false,
  };
  const [values, setValues] = useState(initialValues);

  const onInputChange = (e) => setLabel(e.target.value);

  const onLabelChange = (e) => {
    setValues({ ...values, [e.target.name]: e.target.checked });
  };

  const onFileUpload = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files.length > 0) {
      setFileUpload(e.target.files[0]);
      const objectUrl = URL.createObjectURL(e.target.files[0]);
      setFileName(objectUrl);
      setCheckRemove(false);
    }
  };

  const removeCustom = () => {
    setFileName("");
    setCheckRemove(true);
    setLogoDelete(true);
  };

  const onSubmit = () => {
    let detachable_label_field_array = [];
    for (const [key, value] of Object.entries(values)) {
      if (key !== "is_detachable_label" && key !== "is_signature_required" && key !== "is_deliver_to_po_address" && key !== "is_do_not_safe_drop") {
        if (value === true) detachable_label_field_array.push(key);
      }
    }

    if (checkRemove === false && fileName !== "") {
      let formData = new FormData();
      formData.append("image", fileUpload);
      formData.append("is_detachable_label_enabled", values.is_detachable_label);
      formData.append("is_signature_required", values.is_signature_required);
      formData.append("is_deliver_to_po_address", values.is_deliver_to_po_address);
      formData.append("is_leave_at_door_shipment", values.is_leave_at_door_shipment);
      formData.append("detachable_label_field_array", detachable_label_field_array);
      formData.append("is_do_not_safe_drop", values.is_do_not_safe_drop);
      dispatch(postlabel(profile && profile.id, label, logoDelete, formData));
    } else {
      let formData = {
        detachable_label_field_array: detachable_label_field_array,
        is_detachable_label_enabled: values.is_detachable_label,
        is_signature_required: values.is_signature_required,
        is_deliver_to_po_address: values.is_deliver_to_po_address,
        is_leave_at_door_shipment: values.is_leave_at_door_shipment,
        is_do_not_safe_drop: values.is_do_not_safe_drop
      };
      dispatch(postlabel(profile && profile.id, label, logoDelete, formData));
    }
  };

  useEffect(() => {
    if (labelsettings && labelsettings.loading === false) {
      if (labelsettings?.data?.label_settings) {
        setFileName(labelsettings.data.label_settings.custom_logo_s3_url);
        setFileUpload(labelsettings.data.label_settings.custom_logo_s3_url);
        setLabel(labelsettings.data.label_settings.format);
        setValues({
          ...values,
          is_deliver_to_po_address: labelsettings.data.is_deliver_to_po_address || false,
          is_leave_at_door_shipment: labelsettings.data.is_leave_at_door_shipment || false,
          is_do_not_safe_drop: labelsettings.data.is_do_not_safe_drop || false,
          is_signature_required: labelsettings.data.is_signature_required,
          is_detachable_label: labelsettings.data.label_settings.is_detachable_label,
          order_number: labelsettings.data.label_settings.detachable_label_field_array?.includes("order_number") || false,
          tracking_number: labelsettings.data.label_settings.detachable_label_field_array?.includes("tracking_number") || false,
          reference_number: labelsettings.data.label_settings.detachable_label_field_array?.includes("reference_number") || false,
          carrier_name: labelsettings.data.label_settings.detachable_label_field_array?.includes("carrier_name") || false,
          weight: labelsettings.data.label_settings.detachable_label_field_array?.includes("weight") || false,
        });
      }
      if (labelsettings.message) {
        setAlert(true);
        setOpen(true);
      }
    }
  }, [labelsettings]);

  const handleClose = () => {
    setOpen(false);
    setCheckRemove(true);
    setLogoDelete(false);
    dispatch(resetLabelState());
  };

  useEffect(() => { dispatch(getLabel()); }, []);

  // --- UI Sections ---
  
  let detachableOptions = [];
  for (const [eventKey, eventValue] of Object.entries(values)) {
    if (!["is_detachable_label", "is_signature_required", "is_deliver_to_po_address", "is_leave_at_door_shipment"].includes(eventKey)) {
      detachableOptions.push(
        <FormControlLabel
          key={eventKey}
          control={<Switch checked={eventValue} name={eventKey} onChange={onLabelChange} size="small" />}
          label={<Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 500 }}>{eventKey.replace(/_/, " ")}</Typography>}
          sx={{ width: '45%', m: 0.5 }}
        />
      );
    }
  }

  return (
    <Box sx={{ p: 1 }}>
      {labelsettings && !labelsettings.loading && alert && (
        <Snackbar open={open} anchorOrigin={{ vertical: "top", horizontal: "right" }} autoHideDuration={6000} onClose={handleClose}>
          <Alert severity={labelsettings.hasErrors ? "error" : "success"} sx={{ borderRadius: '12px', fontWeight: 600, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
            {labelsettings.message}
          </Alert>
        </Snackbar>
      )}

      <SettingsContainer elevation={0}>
        {labelsettings && labelsettings.loading && (
          <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, borderRadius: '16px 16px 0 0' }} />
        )}

        <SectionHeader 
          icon={<LabelIcon />} 
          title="Label Customization" 
          subtitle="Configure label formats, delivery requirements, and custom branding for your shipping labels" 
        />
        
        <Divider sx={{ mb: 5, borderColor: '#F1F5F9' }} />

        {labelsettings && !labelsettings.loading && (
          <ConfigWrapper>
            <Stack spacing={4}>
              
              {/* Delivery Requirements */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 2 }}>General Preferences</Typography>
                <Stack spacing={1} sx={{ bgcolor: '#FFFFFF', p: 2, borderRadius: '12px', border: '1px solid #E2E8F0' }}>
                  {profile && profile.show_deliver_to_po_address !== 0 && (
                    <FormControlLabel
                      control={<Switch checked={values.is_deliver_to_po_address} name="is_deliver_to_po_address" onChange={onLabelChange} />}
                      label={<Typography variant="body2" fontWeight={600}>Deliver to Post Office</Typography>}
                    />
                  )}
                  {profile && profile.is_show_leave_at_door_enabled ===true && (
                    <FormControlLabel
                      control={<Switch checked={values.is_leave_at_door_shipment} name="is_leave_at_door_shipment" onChange={onLabelChange} />}
                      label={<Typography variant="body2" fontWeight={600}>Leave at Door</Typography>}
                    />
                  )}
                  {profile && profile.is_show_do_not_safe_drop_enabled ===true && (
                    <FormControlLabel
                      control={<Switch checked={values.is_do_not_safe_drop} name="is_do_not_safe_drop" onChange={onLabelChange} />}
                      label={<Typography variant="body2" fontWeight={600}>Do Not Safe Drop</Typography>}
                    />
                  )}
                  <FormControlLabel
                    control={<Switch checked={values.is_signature_required} name="is_signature_required" onChange={onLabelChange} />}
                    label={<Typography variant="body2" fontWeight={600}>Signature Required</Typography>}
                  />
                  <FormControlLabel
                    control={<Switch checked={values.is_detachable_label} name="is_detachable_label" onChange={onLabelChange} />}
                    label={<Typography variant="body2" fontWeight={600}>Enable Detachable Label</Typography>}
                  />
                </Stack>
              </Box>

              {/* Detachable Fields */}
              {values.is_detachable_label && (
                <Box className="animate-in fade-in duration-500">
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 2 }}>Detachable Label Fields</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', p: 2, bgcolor: '#FFFFFF', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
                    {detachableOptions}
                  </Box>
                </Box>
              )}

              {/* Format & Logo */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 2 }}>Output Configuration</Typography>
                <Stack spacing={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Label Format</InputLabel>
                    <Select
                      value={label}
                      onChange={onInputChange}
                      label="Label Format"
                      sx={{ borderRadius: '12px', bgcolor: '#FFFFFF' }}
                    >
                      <MenuItem value={"pdf"}>Portable Document Format (PDF)</MenuItem>
                      <MenuItem value={"zpl"}>Zebra Programming Language (ZPL)</MenuItem>
                    </Select>
                  </FormControl>

                  <Box sx={{ p: 2.5, border: '1px dashed #CBD5E1', borderRadius: '12px', bgcolor: '#FFFFFF', textAlign: 'center' }}>
                    {!fileName ? (
                      <label htmlFor="contained-button-file">
                        <HiddenInput accept="image/*" id="contained-button-file" onChange={onFileUpload} type="file" />
                        <Button variant="outlined" component="span" startIcon={<UploadIcon />} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}>
                          Upload Custom Logo
                        </Button>
                      </label>
                    ) : (
                      <Box sx={{ position: 'relative', display: 'inline-block' }}>
                        <img src={fileName} alt="custom-logo" style={{ maxHeight: '80px', maxWidth: '200px', borderRadius: '8px', border: '1px solid #E2E8F0' }} />
                        <IconButton 
                          onClick={removeCustom} 
                          sx={{ position: 'absolute', top: -10, right: -10, bgcolor: '#EF4444', color: '#FFF', '&:hover': { bgcolor: '#DC2626' } }} 
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 2, color: '#64748B' }}>
                      <InfoIcon sx={{ fontSize: 16 }} />
                      <Typography variant="caption" fontWeight={500}>Certain labels may not support custom logos.</Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>

              {/* Submit */}
              <Box sx={{ pt: 2, display: 'flex', justifyContent: 'center' }}>
                <Button 
                  variant="contained" 
                  onClick={onSubmit} 
                  startIcon={<SaveIcon />}
                  sx={{ px: 8, py: 1.5, borderRadius: '12px', fontWeight: 800, textTransform: 'none', boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)' }}
                >
                  Save Changes
                </Button>
              </Box>
            </Stack>
          </ConfigWrapper>
        )}
      </SettingsContainer>
    </Box>
  );
};

export default LabelSettingsPage;